import React, { useEffect, useRef, useState } from "react";
import { galleryData } from "../mock";

/** ✅ Self-contained marquee (no Tailwind plugin required) */
const EventMarquee = () => {
  return (
    <div className="bg-black py-4 overflow-hidden border-t border-b border-red-600">
      {/* Track */}
      <div className="marquee-track whitespace-nowrap text-white font-semibold text-lg sm:text-xl tracking-wide">
        <span className="mx-8 text-red-500">
          🥊 Live Boxing Amateur Matches — @Cielo — Stone Park, IL — Oct 23
        </span>
        <span className="mx-8 text-gray-300">|</span>
        <span className="mx-8 text-red-500">
          🥊 Alan Facio — Sports Complex — Hammond, IN — November 15
        </span>

        {/* Duplicate for seamless loop */}
        <span className="mx-8 text-red-500">
          🥊 Live Boxing Amateur Matches — @Cielo — Stone Park, IL — Oct 23
        </span>
        <span className="mx-8 text-gray-300">|</span>
        <span className="mx-8 text-red-500">
          🥊 Alan Facio — Sports Complex — Hammond, IN — November 15
        </span>
      </div>

      {/* Inline CSS to avoid config changes */}
      <style>{`
        .marquee-track {
          display: inline-block;
          min-width: 200%;
          animation: marquee-left 18s linear infinite;
          will-change: transform;
        }
        @keyframes marquee-left {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

/**
 * Create a thumbnail from an actual video frame while avoiding black frames.
 */
const useVideoThumbnail = (src) => {
  const [thumb, setThumb] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";
    video.preload = "auto";
    video.muted = true;
    video.playsInline = true;
    video.src = src;

    const cleanup = () => {
      try { video.pause(); } catch {}
      video.removeAttribute("src");
      try { video.load(); } catch {}
    };

    const averageBrightness = (ctx, w, h) => {
      const sampleW = 160;
      const sampleH = Math.max(1, Math.round((h / w) * sampleW));
      const tmp = document.createElement("canvas");
      tmp.width = sampleW;
      tmp.height = sampleH;
      const tctx = tmp.getContext("2d");
      tctx.drawImage(ctx.canvas, 0, 0, w, h, 0, 0, sampleW, sampleH);
      const data = tctx.getImageData(0, 0, sampleW, sampleH).data;
      let sum = 0;
      for (let i = 0; i < data.length; i += 4) {
        sum += 0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2];
      }
      return sum / (data.length / 4);
    };

    const captureAt = (t) =>
      new Promise((resolve, reject) => {
        const onSeeked = () => {
          try {
            const w = video.videoWidth || 0;
            const h = video.videoHeight || 0;
            if (!w || !h) return reject(new Error("No dimensions"));
            const canvas = document.createElement("canvas");
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, w, h);
            const bright = averageBrightness(ctx, w, h);
            const dataUrl = canvas.toDataURL("image/jpeg");
            resolve({ dataUrl, bright });
          } catch (e) {
            reject(e);
          }
        };
        const onError = () => reject(new Error("Seek error"));
        video.addEventListener("seeked", onSeeked, { once: true });
        video.addEventListener("error", onError, { once: true });
        try { video.currentTime = t; } catch (e) { reject(e); }
      });

    const onLoadedMetadata = async () => {
      try {
        const d = isFinite(video.duration) && video.duration > 0 ? video.duration : 10;
        const candidates = [Math.max(0.2, Math.min(0.5, d * 0.05)), 0.5, 1.0];

        for (const t of candidates) {
          if (video.readyState < 2) {
            await new Promise((r) => {
              const onLoadedData = () => { video.removeEventListener("loadeddata", onLoadedData); r(); };
              video.addEventListener("loadeddata", onLoadedData);
            });
          }
          const { dataUrl, bright } = await captureAt(t);
          if (bright > 8) {
            if (!cancelled) setThumb(dataUrl);
            return;
          }
        }
      } catch {
        /* ignore, fallback poster */
      }
    };

    video.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
    video.addEventListener("error", () => {}, { once: true });

    return () => { cancelled = true; cleanup(); };
  }, [src]);

  return thumb;
};

const Icon = {
  Play: () => <span aria-hidden>▶</span>,
  Pause: () => <span aria-hidden>⏸</span>,
  Close: () => <span aria-hidden>✕</span>,
  Volume: () => <span aria-hidden>🔊</span>,
  VolumeMuted: () => <span aria-hidden>🔇</span>,
  Warning: () => <span aria-hidden>⚠️</span>,
};

const VideoTile = ({ item }) => {
  const videoRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hadError, setHadError] = useState(false);
  const thumb = useVideoThumbnail(item.url);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const tryPlay = () => {
      el.muted = true;
      el.play().catch(() => {});
    };

    const onPlay = () => { setIsPaused(false); setHadError(false); };
    const onPause = () => setIsPaused(true);
    const onError = () => setHadError(true);
    const onCanPlay = () => tryPlay();

    el.addEventListener("play", onPlay);
    el.addEventListener("pause", onPause);
    el.addEventListener("error", onError);
    el.addEventListener("canplay", onCanPlay);

    tryPlay();

    return () => {
      el.removeEventListener("play", onPlay);
      el.removeEventListener("pause", onPause);
      el.removeEventListener("error", onError);
      el.removeEventListener("canplay", onCanPlay);
    };
  }, []);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !("IntersectionObserver" in window)) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
            el.play().catch(() => {});
          } else {
            el.pause();
          }
        });
      },
      { threshold: [0, 0.35, 0.75] }
    );

    io.observe(el);
    return () => io.disconnect();
  }, []);

  const handleTogglePause = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => setHadError(true));
    } else {
      el.pause();
    }
  };

  const handleToggleMute = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setIsMuted(el.muted);
  };

  const handleClose = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    el.muted = true;
    setIsMuted(true);
    setIsPaused(true);
  };

  return (
    <div className="w-full h-full bg-black relative">
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        preload="metadata"
        autoPlay
        muted
        playsInline
        controls
        poster={thumb || "/images/video-thumb.jpg"}
      >
        <source src={item.url} type="video/mp4" />
      </video>

      {/* Custom overlay controls */}
      <div className="absolute top-2 right-2 flex items-center gap-2">
        <button
          onClick={handleClose}
          aria-label="Reset video"
          className="rounded-full bg-black/60 text-white p-2 sm:p-2.5 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white"
        >
          <Icon.Close />
        </button>
        <button
          onClick={handleTogglePause}
          aria-label={isPaused ? "Play" : "Pause"}
          className="rounded-full bg-black/60 text-white p-2 sm:p-2.5 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white"
        >
          {isPaused ? <Icon.Play /> : <Icon.Pause />}
        </button>
        <button
          onClick={handleToggleMute}
          aria-label={isMuted ? "Unmute" : "Mute"}
          className="rounded-full bg-black/60 text-white p-2 sm:p-2.5 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white"
        >
          {isMuted ? <Icon.VolumeMuted /> : <Icon.Volume />}
        </button>
      </div>

      {hadError && (
        <div className="absolute bottom-2 left-2 right-2 text-xs sm:text-sm text-white bg-black/60 px-2 py-1 rounded flex items-start gap-1">
          <span className="mt-[2px]"><Icon.Warning /></span>
          <span>Can’t start playback. Check the file path and codec (H.264 video + AAC audio).</span>
        </div>
      )}
    </div>
  );
};

const PAGE_SIZE = 9;

const GallerySection = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const gridRef = useRef(null);

  const totalPages = Math.max(1, Math.ceil(galleryData.length / PAGE_SIZE));
  const pageItems = galleryData.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const goToPage = (page) => {
    const next = Math.min(Math.max(1, page), totalPages);
    setCurrentPage(next);
    if (gridRef.current) {
      gridRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="gallery" className="py-14 sm:py-16 md:py-20 bg-red-600">
      {/* ✅ Only render once at the top of the section */}
      <EventMarquee />

      <div className="container mx-auto px-3 sm:px-4">
        {/* Header */}
        <div className="max-w-3xl sm:max-w-4xl mx-auto text-center mb-10 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
            Gallery
          </h2>
          <p className="text-gray-300 text-base sm:text-lg md:text-xl">
            See the gym in action — get a feel for the community, culture, and energy.
          </p>
        </div>

        {/* Gallery Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 md:gap-6 max-w-6xl mx-auto scroll-mt-24"
        >
          {pageItems.map((item) => (
            <div
              key={item.id}
              className="group relative overflow-hidden rounded-lg bg-gray-800 aspect-[4/3] sm:aspect-[4/3] md:aspect-square transform transition-all duration-300 hover:scale-[1.02] touch-manipulation"
            >
              {item.type === "image" ? (
                <img
                  src={item.url}
                  alt={item.caption}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  loading="lazy"
                />
              ) : (
                <VideoTile item={item} />
              )}

              {/* Caption overlay (only when a caption exists) */}
              {item.caption ? (
                <div className="pointer-events-none absolute inset-0 bg-black/0 group-hover:bg-black/60 transition-all duration-300 flex items-end">
                  <div className="p-3 sm:p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h4 className="text-white font-semibold text-sm sm:text-base">
                      {item.caption}
                    </h4>
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-8 sm:mt-10 flex items-center justify-center gap-3 sm:gap-4">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 sm:px-5 rounded-full text-sm sm:text-base font-medium bg-black text-white transition-all duration-300 hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>

            <span className="text-white font-semibold text-sm sm:text-base select-none">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 sm:px-5 rounded-full text-sm sm:text-base font-medium bg-black text-white transition-all duration-300 hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          </div>
        )}

        <div className="mt-16 bg-black p-8 rounded-lg max-w-4xl mx-auto text-center">
          <h3 className="text-2xl font-bold text-white mb-4">Want to See More?</h3>
          <p className="text-gray-300 text-lg mb-6">
            Follow us on Instagram @claraboxingacademy for daily updates, training highlights, and behind-the-scenes content!
          </p>
          <div className="flex justify-center gap-4">
            <a
              href="https://www.instagram.com/claraboxingacademy/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-300 inline-block"
            >
              Follow on Instagram
            </a>
            <button className="border border-gray-600 text-gray-300 hover:bg-gray-800 px-6 py-3 rounded-lg font-semibold transition-colors duration-300">
              Visit Us
            </button>
          </div>
        </div>

        <div className="mt-16 grid md:grid-cols-4 gap-8 text-center">
          <div className="bg-black p-4 rounded-lg border border-red-600/20">
            <div className="text-red-600 text-2xl font-bold mb-2">📸</div>
            <h4 className="text-white font-semibold mb-1">Training Photos</h4>
            <p className="text-gray-400 text-sm">Daily session highlights</p>
          </div>
          <div className="bg-black p-4 rounded-lg border border-red-600/20">
            <div className="text-red-600 text-2xl font-bold mb-2">🥊</div>
            <h4 className="text-white font-semibold mb-1">Sparring Highlights</h4>
            <p className="text-gray-400 text-sm">Competitive training moments</p>
          </div>
          <div className="bg-black p-4 rounded-lg border border-red-600/20">
            <div className="text-red-600 text-2xl font-bold mb-2">👶</div>
            <h4 className="text-white font-semibold mb-1">Youth Classes</h4>
            <p className="text-gray-400 text-sm">Young champions in training</p>
          </div>
          <div className="bg-black p-4 rounded-lg border border-red-600/20">
            <div className="text-red-600 text-2xl font-bold mb-2">🏟️</div>
            <h4 className="text-white font-semibold mb-1">Events & Fights</h4>
            <p className="text-gray-400 text-sm">Live Boxing Amateur Matches</p>
            <p className="text-gray-400 text-sm">@Cielo</p>
            <p className="text-gray-400 text-sm">Stone Park, IL</p>
            <p className="text-gray-400 text-sm">Oct 23</p>
            <p className="text-gray-400 text-sm">@Alan Facio</p>
            <p className="text-gray-400 text-sm">Sports Complex</p>
            <p className="text-gray-400 text-sm">Hammond, IN</p>
            <p className="text-gray-400 text-sm">November 15</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GallerySection;
